import React, { useEffect, useRef, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import * as d3 from 'd3';

export type TimeFrame = 'dayOfMonth' | 'weekday' | 'month' | 'year' | 'quarter' | 'weekOfYear';
export type Metric = 'avgReturn' | 'volatility' | 'sharpeRatio' | 'winRate' | 'maxDrawdown';

export type HeatmapDataPoint = {
    period: string;
    clusterId: number;
    value: number;
    count: number;
    rawReturns: number[];
    timestamps: string[];
};

interface TimeHeatMapProps {
    data: HeatmapDataPoint[];
    timeFrame: TimeFrame;
    metric: Metric;
    clusterIds: number[];
    onCellHover: (cell: HeatmapDataPoint | null) => void;
}

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const TimeHeatMap: React.FC<TimeHeatMapProps> = ({
    data,
    timeFrame,
    metric,
    clusterIds,
    onCellHover
}) => {
    const theme = useTheme();
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(800);

    // Handle container resizing
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Get period label
    const getPeriodLabel = (period: string): string => {
        switch (timeFrame) {
            case 'weekday':
                return WEEKDAY_LABELS[parseInt(period)] || period;
            case 'month':
                return MONTH_LABELS[parseInt(period)] || period;
            case 'quarter':
                return `Q${parseInt(period) + 1}`;
            case 'dayOfMonth':
                return period;
            case 'weekOfYear':
                return `${period}`;
            case 'year':
                return period;
            default:
                return period;
        }
    };

    // Render the heatmap
    useEffect(() => {
        if (!svgRef.current || data.length === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const margin = { top: 20, right: 0, bottom: 40, left: 60 };
        const height = 400 - margin.top - margin.bottom;
        const innerWidth = width - margin.left - margin.right;

        const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

        // Get unique periods and sort them
        const uniquePeriods = [...new Set(data.map(d => d.period))].sort((a, b) => parseInt(a) - parseInt(b));
        const uniqueClusters = clusterIds.sort((a, b) => a - b);

        // Create matrix structure: clusters as rows, periods as columns (+1 for averages)
        const rows = uniqueClusters.length + 1; // +1 for average row
        const cols = uniquePeriods.length + 1; // +1 for average column

        if (rows === 1 || cols === 1) return;

        const cellWidth = innerWidth / cols;
        const cellHeight = height / rows;

        // Create a lookup map for quick data access
        const dataMap = new Map<string, HeatmapDataPoint>();
        data.forEach(d => {
            dataMap.set(`${d.clusterId}-${d.period}`, d);
        });

        // Calculate averages
        const calculateClusterAverage = (clusterId: number): number => {
            const clusterData = data.filter(d => d.clusterId === clusterId);
            if (clusterData.length === 0) return 0;

            if (metric === 'avgReturn' || metric === 'sharpeRatio') {
                // For returns and Sharpe, average the values directly
                return clusterData.reduce((sum, d) => sum + d.value, 0) / clusterData.length;
            } else if (metric === 'volatility' || metric === 'winRate' || metric === 'maxDrawdown') {
                // For other metrics, combine raw returns and recalculate
                const allReturns = clusterData.flatMap(d => d.rawReturns);
                return calculateMetricFromReturns(allReturns, metric);
            }
            return 0;
        };

        const calculatePeriodAverage = (period: string): number => {
            const periodData = data.filter(d => d.period === period);
            if (periodData.length === 0) return 0;

            if (metric === 'avgReturn' || metric === 'sharpeRatio') {
                return periodData.reduce((sum, d) => sum + d.value, 0) / periodData.length;
            } else {
                const allReturns = periodData.flatMap(d => d.rawReturns);
                return calculateMetricFromReturns(allReturns, metric);
            }
        };

        const calculateOverallAverage = (): number => {
            if (data.length === 0) return 0;

            if (metric === 'avgReturn' || metric === 'sharpeRatio') {
                return data.reduce((sum, d) => sum + d.value, 0) / data.length;
            } else {
                const allReturns = data.flatMap(d => d.rawReturns);
                return calculateMetricFromReturns(allReturns, metric);
            }
        };

        const calculateMetricFromReturns = (returns: number[], metricType: Metric): number => {
            if (returns.length === 0) return 0;

            switch (metricType) {
                case 'avgReturn':
                    return returns.reduce((sum, r) => sum + r, 0) / returns.length * 100;
                case 'volatility':
                    const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
                    return Math.sqrt(returns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / returns.length) * 100;
                case 'sharpeRatio':
                    const avg = returns.reduce((sum, r) => sum + r, 0) / returns.length;
                    const std = Math.sqrt(returns.reduce((sum, r) => sum + Math.pow(r - avg, 2), 0) / returns.length);
                    return std === 0 ? 0 : avg / std;
                case 'winRate':
                    return returns.filter(r => r > 0).length / returns.length * 100;
                case 'maxDrawdown':
                    let peak = 1, maxDD = 0, cumulative = 1;
                    returns.forEach(r => {
                        cumulative *= (1 + r);
                        if (cumulative > peak) peak = cumulative;
                        const dd = (peak - cumulative) / peak;
                        if (dd > maxDD) maxDD = dd;
                    });
                    return maxDD * 100;
                default:
                    return 0;
            }
        };

        // Calculate averages
        const clusterAverages = new Map<number, number>();
        uniqueClusters.forEach(clusterId => {
            clusterAverages.set(clusterId, calculateClusterAverage(clusterId));
        });

        const periodAverages = new Map<string, number>();
        uniquePeriods.forEach(period => {
            periodAverages.set(period, calculatePeriodAverage(period));
        });

        const overallAverage = calculateOverallAverage();

        // Get all values including averages for color scale
        const allValues = [
            ...data.map(d => d.value),
        ];

        const extent = d3.extent(allValues) as [number, number];

        // Color scales - separate for regular data and averages
        const regularValues = data.map(d => d.value);
        const regularExtent = d3.extent(regularValues) as [number, number];

        const averageValues = [
            ...Array.from(clusterAverages.values()),
            ...Array.from(periodAverages.values()),
            overallAverage
        ];
        const averageExtent = d3.extent(averageValues) as [number, number];

        console.log(averageExtent)

        let colorScale: d3.ScaleSequential<string>;
        let averageColorScale: d3.ScaleSequential<string>;

        if (metric === 'avgReturn' || metric === 'sharpeRatio' || metric === 'winRate') {
            if (metric === 'winRate') {
                // Use percentiles for win rate
                const sortedValues = allValues.sort((a, b) => a - b);
                const p10 = d3.quantile(sortedValues, 0.1) || 0;
                const p90 = d3.quantile(sortedValues, 0.9) || 100;
                const center = 50;
                const maxDistance = Math.max(Math.abs(p10 - center), Math.abs(p90 - center));
                colorScale = d3.scaleSequential(d3.interpolateRdYlGn)
                    .domain([center - maxDistance, center + maxDistance]);
            } else {
                // Use 10th and 90th percentiles for returns/Sharpe
                const sortedValues = allValues.sort((a, b) => a - b);
                const p10 = d3.quantile(sortedValues, 0.1) || extent[0];
                const p90 = d3.quantile(sortedValues, 0.9) || extent[1];
                const maxAbs = Math.max(Math.abs(p10), Math.abs(p90));
                colorScale = d3.scaleSequential(d3.interpolateRdYlGn)
                    .domain([-maxAbs, maxAbs]);
            }
        } else {
            // Use percentiles for volatility and max drawdown
            const sortedValues = allValues.sort((a, b) => a - b);
            const p10 = d3.quantile(sortedValues, 0.1) || extent[0];
            const p90 = d3.quantile(sortedValues, 0.9) || extent[1];
            colorScale = d3.scaleSequential(d3.interpolateYlOrRd)
                .domain([p10, p90]);
        }

        // Average cells color scale (using actual extent for better color range)
        if (metric === 'avgReturn' || metric === 'sharpeRatio' || metric === 'winRate') {
            if (metric === 'winRate') {
                const center = 50;
                const maxDistance = Math.max(Math.abs(averageExtent[0] - center), Math.abs(averageExtent[1] - center));
                averageColorScale = d3.scaleSequential(d3.interpolateRdYlGn)
                    .domain([center - maxDistance, center + maxDistance]);
            } else {
                // Use actual extent for returns/Sharpe to get full color range
                averageColorScale = d3.scaleSequential(d3.interpolateRdYlGn)
                    .domain([averageExtent[0], averageExtent[1]]); // [-0.467, 1.886]
            }
        } else {
            // Use actual extent for volatility and max drawdown
            averageColorScale = d3.scaleSequential(d3.interpolateYlOrRd)
                .domain(averageExtent);
        }

        // Create matrix of cells including averages
        const matrixData: Array<{
            clusterId: number | 'avg',
            period: string | 'avg',
            rowIndex: number,
            colIndex: number,
            data?: HeatmapDataPoint,
            value?: number,
            isAverage?: boolean
        }> = [];

        // Regular cells
        uniqueClusters.forEach((clusterId, rowIndex) => {
            uniquePeriods.forEach((period, colIndex) => {
                const cellData = dataMap.get(`${clusterId}-${period}`);
                matrixData.push({
                    clusterId,
                    period,
                    rowIndex,
                    colIndex,
                    data: cellData
                });
            });

            // Row averages (last column)
            matrixData.push({
                clusterId,
                period: 'avg',
                rowIndex,
                colIndex: uniquePeriods.length,
                value: clusterAverages.get(clusterId),
                isAverage: true
            });
        });

        // Column averages (last row)
        uniquePeriods.forEach((period, colIndex) => {
            matrixData.push({
                clusterId: 'avg',
                period,
                rowIndex: uniqueClusters.length,
                colIndex,
                value: periodAverages.get(period),
                isAverage: true
            });
        });

        // Overall average (bottom-right corner)
        matrixData.push({
            clusterId: 'avg',
            period: 'avg',
            rowIndex: uniqueClusters.length,
            colIndex: uniquePeriods.length,
            value: overallAverage,
            isAverage: true
        });

        // Create cells
        const cells = g.selectAll('.cell')
            .data(matrixData)
            .join('rect')
            .attr('class', 'cell')
            .attr('x', d => d.colIndex * cellWidth + 1)
            .attr('y', d => d.rowIndex * cellHeight + 1)
            .attr('width', cellWidth - 2)
            .attr('height', cellHeight - 2)
            .attr('fill', d => {
                if (d.isAverage && d.value !== undefined) {
                    return averageColorScale(d.value);
                } else if (d.data) {
                    return colorScale(d.data.value);
                } else {
                    return theme.palette.grey[800];
                }
            })
            .attr('stroke', theme.palette.divider)
            .attr('stroke-width', d => d.isAverage ? 1 : 0.5)
            .attr('rx', 1)
            .style('cursor', d => (d.data || d.isAverage) ? 'pointer' : 'default')
            .on('mouseover', function (event, d) {
                if (d.data) {
                    onCellHover(d.data);
                } else if (d.isAverage) {
                    // Create fake data point for average hover
                    const avgData: HeatmapDataPoint = {
                        period: d.period as string,
                        clusterId: d.clusterId as number,
                        value: d.value!,
                        count: 0,
                        rawReturns: [],
                        timestamps: []
                    };
                    onCellHover(avgData);
                }
                d3.select(this)
                    .attr('stroke', theme.palette.primary.main)
                    .attr('stroke-width', 2);
            })
            .on('mouseout', function (event, d) {
                onCellHover(null);
                d3.select(this)
                    .attr('stroke', theme.palette.divider)
                    .attr('stroke-width', d.isAverage ? 1 : 0.5);
            });

        // Add cell values
        g.selectAll('.cell-value')
            .data(matrixData.filter(d => d.data || d.isAverage))
            .join('text')
            .attr('class', 'cell-value')
            .attr('x', d => d.colIndex * cellWidth + cellWidth / 2)
            .attr('y', d => d.rowIndex * cellHeight + cellHeight / 2)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .style('fill', '#000000')
            .style('font-size', Math.min(cellWidth, cellHeight) < 40 ? '12px' : '13px')
            .style('font-weight', d => d.isAverage ? 'bold' : 'normal')
            .style('pointer-events', 'none')
            .text(d => {
                const value = d.isAverage ? d.value! : d.data!.value;
                if (metric === 'avgReturn' || metric === 'volatility' || metric === 'winRate' || metric === 'maxDrawdown') {
                    return `${value.toFixed(1)}%`;
                } else {
                    return value.toFixed(2);
                }
            });

        /*
        // Add arrows for comparison with averages (add this after the cell values section)
        g.selectAll('.comparison-arrow')
            .data(matrixData.filter(d => d.data && !d.isAverage)) // Only regular cells, not averages
            .join('text')
            .attr('class', 'comparison-arrow')
            .attr('x', d => d.colIndex * cellWidth + cellWidth / 2)
            .attr('y', d => {
                if (!d.data) return 0;
                const periodAvg = periodAverages.get(d.period as string) || 0;
                const cellValue = d.data.value;
                const isAboveAverage = cellValue > periodAvg;

                // If above average, arrow goes above text; if below, arrow goes below text
                return d.rowIndex * cellHeight + cellHeight / 2 + (isAboveAverage ? -15 : 15);
            })
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .style('font-size', '18px')
            .style('pointer-events', 'none')
            .style('fill', d => {
                if (!d.data) return 'transparent';

                const periodAvg = periodAverages.get(d.period as string) || 0;
                const cellValue = d.data.value;

                const higherIsBetter = metric === 'avgReturn' || metric === 'sharpeRatio' || metric === 'winRate';
                const isAboveAverage = cellValue > periodAvg;

                if (higherIsBetter) {
                    return isAboveAverage ? '#4caf50' : '#f44336';
                } else {
                    return isAboveAverage ? '#f44336' : '#4caf50';
                }
            })
            .text(d => {
                if (!d.data) return '';

                const periodAvg = periodAverages.get(d.period as string) || 0;
                const cellValue = d.data.value;

                const higherIsBetter = metric === 'avgReturn' || metric === 'sharpeRatio' || metric === 'winRate';
                const isAboveAverage = cellValue > periodAvg;

                if (higherIsBetter) {
                    return isAboveAverage ? '▲' : '▼';
                } else {
                    return isAboveAverage ? '▼' : '▲';
                }
            });

        // Add row comparison arrows (vs cluster average)
        g.selectAll('.row-comparison-arrow')
            .data(matrixData.filter(d => d.data && !d.isAverage)) // Only regular cells
            .join('text')
            .attr('class', 'row-comparison-arrow')
            .attr('x', d => {
                if (!d.data) return 0;
                const clusterAvg = clusterAverages.get(d.clusterId as number) || 0;
                const cellValue = d.data.value;
                const isAboveAverage = cellValue > clusterAvg;

                // If above average, arrow goes right of text; if below, arrow goes left of text
                return d.colIndex * cellWidth + cellWidth / 2 + (isAboveAverage ? 25 : -25);
            })
            .attr('y', d => d.rowIndex * cellHeight + cellHeight / 2 - 1)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .style('font-size', '14px')
            .style('pointer-events', 'none')
            .style('fill', d => {
                if (!d.data) return 'transparent';

                const clusterAvg = clusterAverages.get(d.clusterId as number) || 0;
                const cellValue = d.data.value;

                const higherIsBetter = metric === 'avgReturn' || metric === 'sharpeRatio' || metric === 'winRate';
                const isAboveAverage = cellValue > clusterAvg;

                if (higherIsBetter) {
                    return isAboveAverage ? '#4caf50' : '#f44336';
                } else {
                    return isAboveAverage ? '#f44336' : '#4caf50';
                }
            })
            .text(d => {
                if (!d.data) return '';

                const clusterAvg = clusterAverages.get(d.clusterId as number) || 0;
                const cellValue = d.data.value;

                const higherIsBetter = metric === 'avgReturn' || metric === 'sharpeRatio' || metric === 'winRate';
                const isAboveAverage = cellValue > clusterAvg;

                if (higherIsBetter) {
                    return isAboveAverage ? '▶' : '◀';  // Changed to ▶ and ◀
                } else {
                    return isAboveAverage ? '◀' : '▶';
                }
            });
            */

        // Add row labels (clusters + average)
        const rowLabels = [...uniqueClusters.map(id => `Cluster ${id}`), 'Average'];
        g.selectAll('.row-label')
            .data(rowLabels)
            .join('text')
            .attr('class', 'row-label')
            .attr('x', -10)
            .attr('y', (d, i) => i * cellHeight + cellHeight / 2)
            .attr('text-anchor', 'end')
            .attr('dominant-baseline', 'middle')
            .style('fill', theme.palette.text.primary)
            .style('font-size', '11px')
            .style('font-weight', (d, i) => i === rowLabels.length - 1 ? 'bold' : 'bold')
            .text(d => d);

        // Add column labels (time periods + average)
        const colLabels = [...uniquePeriods.map(p => getPeriodLabel(p)), 'Avg'];
        g.selectAll('.col-label')
            .data(colLabels)
            .join('text')
            .attr('class', 'col-label')
            .attr('x', (d, i) => i * cellWidth + cellWidth / 2)
            .attr('y', -5)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .style('fill', theme.palette.text.primary)
            .style('font-size', '11px')
            .style('font-weight', (d, i) => i === colLabels.length - 1 ? 'bold' : 'bold')
            .text(d => d);

    }, [data, width, timeFrame, metric, theme, onCellHover, clusterIds]);

    return (
        <Box ref={containerRef} sx={{ width: '100%', overflowX: 'hidden' }}>
            <svg
                ref={svgRef}
                width={width}
                height={400}
                style={{ background: 'transparent' }}
            />
        </Box>
    );
};